/**
 * Overlays.jsx
 *
 * Pure / presentational building-blocks:
 *   • All SVG icon primitives (no emoji anywhere)
 *   • ProFeatureIcon  — resolves PRO_FEATURES icon keys → SVG
 *   • CueLabelIcon    — resolves QUICK_CUES icon keys → SVG
 *   • CopyButton
 *   • VUMeter
 *   • CueOverlay
 *
 * Gold palette:
 *   --g:  #d4af37   (base gold)
 *   --gl: #f1d279   (light gold highlight)
 */

import React, { useState, useEffect, useRef, useCallback } from "react";

// ─────────────────────────────────────────────────────────────
//  Generic icon shell
// ─────────────────────────────────────────────────────────────
export const Ico = ({ d, fill, children, size = 15 }) => (
  <svg
    viewBox="0 0 24 24" width={size} height={size}
    fill={fill || "none"} stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
  >
    {d ? <path d={d} /> : children}
  </svg>
);

// ─────────────────────────────────────────────────────────────
//  UI control icons
// ─────────────────────────────────────────────────────────────
export const QRIcon = ({ size = 15 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3"  y="3"  width="7" height="7" rx="1" />
    <rect x="14" y="3"  width="7" height="7" rx="1" />
    <rect x="3"  y="14" width="7" height="7" rx="1" />
    <rect x="5.5"  y="5.5"  width="2" height="2" fill="currentColor" stroke="none" />
    <rect x="16.5" y="5.5"  width="2" height="2" fill="currentColor" stroke="none" />
    <rect x="5.5"  y="16.5" width="2" height="2" fill="currentColor" stroke="none" />
    <path d="M14 14h2v2h-2zM18 14h2v2h-2zM14 18h2v2h-2zM18 18h2v2h-2zM16 16h2v2h-2z" fill="currentColor" stroke="none" />
  </svg>
);

export const CopyIcon = ({ size = 14 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

export const CheckIcon = ({ size = 14 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const MicIcon = ({ size = 14 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8"  y1="23" x2="16" y2="23" />
  </svg>
);

export const LockIcon = ({ size = 13 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

export const CloseIcon = ({ size = 16 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6"  y1="6" x2="18" y2="18" />
  </svg>
);

export const ChevronRightIcon = ({ size = 11 }) => (
  <svg viewBox="0 0 20 20" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M7 5l5 5-5 5" />
  </svg>
);

export const ShareIcon = ({ size = 15 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5"  r="3" />
    <circle cx="6"  cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59"  y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51"  x2="8.59"  y2="10.49" />
  </svg>
);

export const CopyClipboardIcon = ({ size = 15 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

export const FullscreenEnterIcon = ({ size = 15 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
  </svg>
);

export const FullscreenExitIcon = ({ size = 15 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 0 2 2v3" />
  </svg>
);

export const EditIcon = ({ size = 15 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

export const MirrorIcon = ({ size = 15 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v18M4 7l4 5-4 5M20 7l-4 5 4 5" />
  </svg>
);

export const SectionsIcon = ({ size = 15 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3"  y1="6"  x2="21" y2="6"  />
    <line x1="3"  y1="12" x2="15" y2="12" />
    <line x1="3"  y1="18" x2="18" y2="18" />
  </svg>
);

export const NotesIcon = ({ size = 15 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

export const HelpIcon = ({ size = 15 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

export const GridIcon = ({ size = 15 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3"  y="3"  width="7" height="7" rx="1" />
    <rect x="14" y="3"  width="7" height="7" rx="1" />
    <rect x="3"  y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

export const StopIcon = ({ size = 15 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" stroke="none">
    <rect x="4" y="4" width="16" height="16" rx="2" />
  </svg>
);

export const PlayIcon = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" stroke="none">
    <polygon points="6,3 20,12 6,21" />
  </svg>
);

export const PauseIcon = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" stroke="none">
    <rect x="5"  y="3" width="5" height="18" rx="1" />
    <rect x="14" y="3" width="5" height="18" rx="1" />
  </svg>
);

export const SkipBackIcon = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="19,20 9,12 19,4" />
    <line x1="5" y1="19" x2="5" y2="5" />
  </svg>
);

export const SkipForwardIcon = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="5,4 15,12 5,20" />
    <line x1="19" y1="5" x2="19" y2="19" />
  </svg>
);

// ── Media / device icons ──────────────────────────────────────
export const CamIcon = ({ size = 13 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

export const ScrIcon = ({ size = 13 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <line x1="8"  y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

export const RecIcon = ({ recording }) => (
  <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="8" />
    <circle cx="12" cy="12" r="3.5" fill={recording ? "#e05555" : "currentColor"} stroke="none" />
  </svg>
);

export const StopRecIcon = () => (
  <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="8" />
    <rect x="9" y="9" width="6" height="6" rx="1" fill="currentColor" stroke="none" />
  </svg>
);

export const DlIcon = ({ size = 14 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7,10 12,15 17,10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

export const SignalIcon = ({ size = 13 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1.5 8.5a13 13 0 0 1 21 0" />
    <path d="M5  12a9  9 0 0 1 14 0" />
    <path d="M8.5 15.5a5 5 0 0 1 7 0" />
    <circle cx="12" cy="19" r="1" fill="currentColor" stroke="none" />
  </svg>
);

export const SparkleIcon = ({ size = 13 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6L12 2z" />
  </svg>
);

// ─────────────────────────────────────────────────────────────
//  Icon resolvers used by Modals + RemoteComponents
// ─────────────────────────────────────────────────────────────

/** Resolves PRO_FEATURES icon key → small SVG */
export function ProFeatureIcon({ name, size = 14 }) {
  const s = size;
  switch (name) {
    case "mic":    return <MicIcon size={s} />;
    case "camera": return <CamIcon size={s} />;
    case "screen": return <ScrIcon size={s} />;
    case "qr":     return <QRIcon  size={s} />;
    case "cue":    return <GridIcon size={s} />;
    case "doc":
      return (
        <svg viewBox="0 0 24 24" width={s} height={s} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14,2 14,8 20,8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      );
    default: return null;
  }
}

/** Resolves QUICK_CUES icon key → small SVG (inherits `color` from parent) */
export function CueLabelIcon({ name, size = 14 }) {
  const s = size;
  const base = { width: s, height: s, fill: "none", stroke: "currentColor", strokeWidth: 1.75, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (name) {
    case "standby":
      return (
        <svg viewBox="0 0 24 24" {...base}>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <circle cx="12" cy="16" r=".5" fill="currentColor" stroke="none" />
        </svg>
      );
    case "onair":
      return (
        <svg viewBox="0 0 24 24" {...base}>
          <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" />
          <path d="M6.3 6.3a8 8 0 0 0 0 11.4M17.7 6.3a8 8 0 0 1 0 11.4" />
        </svg>
      );
    case "timer":
      return (
        <svg viewBox="0 0 24 24" {...base}>
          <circle cx="12" cy="13" r="8" />
          <polyline points="12,9 12,13 15,13" />
          <line x1="9"  y1="2" x2="15" y2="2" />
          <line x1="12" y1="2" x2="12" y2="5" />
        </svg>
      );
    case "mic":
      return <MicIcon size={s} />;
    case "cut":
      return (
        <svg viewBox="0 0 24 24" {...base}>
          <circle cx="6"  cy="6"  r="3" />
          <circle cx="6"  cy="18" r="3" />
          <line x1="20" y1="4"  x2="8.12" y2="15.88" />
          <line x1="14.47" y1="14.48" x2="20" y2="20" />
          <line x1="8.12"  y1="8.12"  x2="12" y2="12" />
        </svg>
      );
    case "hold":
      return (
        <svg viewBox="0 0 24 24" {...base}>
          <rect x="5"  y="3" width="4" height="18" rx="1" />
          <rect x="15" y="3" width="4" height="18" rx="1" />
        </svg>
      );
    case "clear":
      return (
        <svg viewBox="0 0 24 24" {...base}>
          <polyline points="20,6 9,17 4,12" />
        </svg>
      );
    case "quiet":
      return (
        <svg viewBox="0 0 24 24" {...base}>
          <path d="M9 9v6h4l5 5V4L13 9H9z" />
          <line x1="1"  y1="1"  x2="23" y2="23" />
        </svg>
      );
    case "thumbsup":
      return (
        <svg viewBox="0 0 24 24" {...base}>
          <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z" />
          <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
        </svg>
      );
    case "again":
      return (
        <svg viewBox="0 0 24 24" {...base}>
          <polyline points="1,4 1,10 7,10" />
          <path d="M3.51 15a9 9 0 1 0 .49-4.44" />
        </svg>
      );
    default:
      return null;
  }
}

// Remote-specific SVG icons (used in StandaloneRemote / WebRTCModal banners)
export const RemoteIcon = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2" />
    <circle cx="12" cy="17" r="1" fill="currentColor" stroke="none" />
    <line x1="9" y1="7"  x2="15" y2="7"  />
    <line x1="9" y1="11" x2="13" y2="11" />
  </svg>
);

export const TVIcon = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="15" rx="2" />
    <polyline points="17,2 12,7 7,2" />
  </svg>
);

export const ScanIcon = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2" />
    <line x1="3" y1="12" x2="21" y2="12" />
  </svg>
);

export const LinkIcon = ({ size = 14 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

export const AlertIcon = ({ size = 16 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9"  x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

export const CheckCircleIcon = ({ size = 16 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22,4 12,14.01 9,11.01" />
  </svg>
);

export const ZapIcon = ({ size = 16 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13,2 3,14 12,14 11,22 21,10 12,10 13,2" />
  </svg>
);

export const StepIcon = ({ n, size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <text x="12" y="16" textAnchor="middle" fontSize="11" fill="currentColor" stroke="none" fontFamily="IBM Plex Mono, monospace" fontWeight="600">{n}</text>
  </svg>
);

// ─────────────────────────────────────────────────────────────
//  CopyButton
// ─────────────────────────────────────────────────────────────
export function CopyButton({ text, label = "Copy Code" }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      style={{
        display: "flex", alignItems: "center", gap: 6,
        padding: "8px 16px",
        background: copied ? "rgba(110,207,110,.12)" : "var(--g)",
        border: `1px solid ${copied ? "rgba(110,207,110,.3)" : "var(--g)"}`,
        borderRadius: 7,
        color: copied ? "#6ecf6e" : "#0c0c0c",
        fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 600,
        letterSpacing: ".08em", textTransform: "uppercase",
        cursor: "pointer", transition: "all .2s",
        width: "100%", justifyContent: "center",
      }}
    >
      {copied ? <CheckIcon size={13} /> : <CopyIcon size={13} />}
      {copied ? "Copied!" : label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
//  VUMeter
// ─────────────────────────────────────────────────────────────
export function VUMeter({ level, active }) {
  const bars = 12;
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 20 }}>
      {Array.from({ length: bars }).map((_, i) => {
        const threshold = (i + 1) / bars;
        const lit       = active && level >= threshold;
        const color     = i >= 9 ? "#ef5350" : i >= 6 ? "#ffd54f" : "#66bb6a";
        return (
          <div key={i} style={{
            width: 3,
            height: `${40 + (i / bars) * 60}%`,
            background: lit ? color : "rgba(255,255,255,.1)",
            borderRadius: 1,
            transition: "background .05s",
          }} />
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  CueOverlay
//
//  Full-screen centred broadcast card.
//  - Auto-dismisses after CUE_DURATION ms with no touch needed
//  - SVG countdown ring shows remaining time
//  - Bold, readable at distance — designed for on-set use
//  - Slide-in from top, fade-scale-out on dismiss
//  - Clicking anywhere dismisses early
// ─────────────────────────────────────────────────────────────
export const CUE_DURATION = 6000;

const RING_R  = 22;   // SVG circle radius
const RING_C  = 2 * Math.PI * RING_R; // circumference

export function CueOverlay({ cue, onDismiss }) {
  const [progress,    setProgress]    = useState(1);   // 0→1 remaining fraction
  const [exiting,     setExiting]     = useState(false);
  const rafRef        = useRef(null);
  const startRef      = useRef(null);
  const dismissedRef  = useRef(false);

  const triggerDismiss = useCallback(() => {
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    cancelAnimationFrame(rafRef.current);
    setExiting(true);
    setTimeout(onDismiss, 320);
  }, [onDismiss]);

  useEffect(() => {
    if (!cue) return;
    dismissedRef.current = false;
    setExiting(false);
    setProgress(1);
    startRef.current = performance.now();

    const tick = (now) => {
      const frac = Math.max(0, 1 - (now - startRef.current) / CUE_DURATION);
      setProgress(frac);
      if (frac > 0) rafRef.current = requestAnimationFrame(tick);
      else triggerDismiss();
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [cue, triggerDismiss]);

  if (!cue) return null;

  // Countdown ring dashoffset: full ring when progress=1, empty when 0
  const dashOffset = RING_C * (1 - progress);
  const secsLeft   = Math.ceil((CUE_DURATION / 1000) * progress);

  return (
    <div
      className={`tp-cue-overlay${exiting ? " tp-cue-overlay-exit" : ""}`}
      onClick={triggerDismiss}
      role="alert"
      aria-live="assertive"
    >
      {/* Backdrop blur layer */}
      <div className="tp-cue-backdrop" style={{ "--cue-bg": cue.bg }} />

      {/* Card */}
      <div
        className="tp-cue-card"
        style={{ "--cue-accent": cue.accent, "--cue-bg": cue.bg }}
        onClick={e => e.stopPropagation()}
      >
        {/* Top accent bar */}
        <div className="tp-cue-card-topbar" style={{ background: cue.accent }} />

        <div className="tp-cue-card-inner">
          {/* Icon */}
          <div className="tp-cue-card-icon" style={{ color: cue.accent }}>
            <CueLabelIcon name={cue.icon} size={32} />
          </div>

          {/* Label */}
          <div className="tp-cue-card-label" style={{ color: cue.accent }}>
            {cue.label}
          </div>

          {/* Countdown ring + seconds */}
          <div className="tp-cue-card-ring-wrap">
            <svg width={54} height={54} viewBox="0 0 54 54" style={{ transform: "rotate(-90deg)" }}>
              {/* Track */}
              <circle
                cx={27} cy={27} r={RING_R}
                fill="none"
                stroke="rgba(255,255,255,.08)"
                strokeWidth={3}
              />
              {/* Progress */}
              <circle
                cx={27} cy={27} r={RING_R}
                fill="none"
                stroke={cue.accent}
                strokeWidth={3}
                strokeLinecap="round"
                strokeDasharray={RING_C}
                strokeDashoffset={dashOffset}
                style={{ transition: "stroke-dashoffset .08s linear" }}
              />
            </svg>
            <span className="tp-cue-card-secs" style={{ color: cue.accent }}>
              {secsLeft}
            </span>
          </div>

          <span className="tp-cue-card-hint">Tap anywhere to dismiss early</span>
        </div>

        {/* Bottom progress bar */}
        <div className="tp-cue-card-bar-bottom">
          <div
            className="tp-cue-card-bar-fill"
            style={{ width: `${progress * 100}%`, background: cue.accent }}
          />
        </div>
      </div>
    </div>
  );
}